import { Icon } from '@dixa/lib-shared-habla';
import { animated, config, useSpring, useSprings } from '@react-spring/web';
import { Channel } from 'lib-shared-types-channels';
import moment from 'moment-timezone';
import { memo, useEffect, useMemo, useState } from 'react';
import CountUp from 'react-countup';

import { channelAttributes } from 'utils/channels';

import { useRewindContext } from '../../../rewindModal.context';
import { RewindSlideProps } from '../../../rewindModal.types';
import { SlideContainer } from '../../slideContainer';

import styles from './styleSlide.module.scss';

// Animation timing constants (in ms from slide start)
const TIMING = {
  // Phase 1: Big title appears centered
  TITLE_START: 300,
  // Phase 2: Title moves to top
  TITLE_MOVE_TO_TOP: 2000,
  // Phase 3: Subtitle appears and slides up from below title
  SUBTITLE_APPEAR: 2500,
  // Phase 4+: Podium appears
  PODIUM_START: 3500,
  PODIUM_STAGGER: 1200, // Time between each podium position
  // Phase 8-10: Rhythm cards one by one
  RHYTHM_CARD_1: 8500,
  RHYTHM_CARD_2: 10000,
  RHYTHM_CARD_3: 11500,
};

// Catchy alliterative taglines for #1 position (2nd word starts with same letter as channel)
const CHANNEL_TAGLINES: Record<Channel, string> = {
  widget_chat: 'Chat Champion',
  email: 'Email Expert',
  pstn_phone: 'Phone Phenomenon',
  voicemail: 'Voice Virtuoso',
  callback: 'Callback Captain',
  messenger: 'Messenger Maestro',
  facebook_messenger: 'Facebook Fanatic',
  whats_app: 'WhatsApp Wizard',
  contact_form: 'Form Fanatic',
  sms: 'Text Titan',
  twitter: 'Twitter Titan',
  adachat: 'Ada Ace',
  digitalgeniuschat: 'Digital Dynamo',
  kindlychat: 'Kindly King',
  trustpilotreviews: 'Trustpilot Trailblazer',
};

const getChannelConfig = (channel: string) => {
  const attributes =
    channelAttributes[channel as keyof typeof channelAttributes];
  const tagline = CHANNEL_TAGLINES[channel] || 'Channel Champion';

  return {
    icon: attributes?.icon ?? 'chat',
    name: attributes?.name ?? channel.replace(/_/g, ' '),
    tagline,
  };
};

export const StyleSlide = memo(function StyleSlide({
  isCompleted,
}: RewindSlideProps): JSX.Element {
  const { data } = useRewindContext();
  const [phase, setPhase] = useState(isCompleted ? 10 : 0);

  // Start animation sequence
  useEffect(() => {
    // If slide has completed before, skip animations
    if (isCompleted) {
      setPhase(10);
      return;
    }

    const timers = [
      setTimeout(() => setPhase(1), TIMING.TITLE_START),
      setTimeout(() => setPhase(2), TIMING.TITLE_MOVE_TO_TOP),
      setTimeout(() => setPhase(3), TIMING.SUBTITLE_APPEAR),
      setTimeout(() => setPhase(4), TIMING.PODIUM_START),
      setTimeout(
        () => setPhase(5),
        TIMING.PODIUM_START + TIMING.PODIUM_STAGGER,
      ),
      setTimeout(
        () => setPhase(6),
        TIMING.PODIUM_START + TIMING.PODIUM_STAGGER * 2,
      ),
      setTimeout(
        () => setPhase(7),
        TIMING.PODIUM_START + TIMING.PODIUM_STAGGER * 3,
      ),
      // Rhythm cards appear one by one
      setTimeout(() => setPhase(8), TIMING.RHYTHM_CARD_1),
      setTimeout(() => setPhase(9), TIMING.RHYTHM_CARD_2),
      setTimeout(() => setPhase(10), TIMING.RHYTHM_CARD_3),
    ];

    return () => timers.forEach(clearTimeout);
  }, [isCompleted]);

  const getTimeOfDayLabel = (
    hour: number,
  ): { label: string; emoji: string } => {
    if (hour >= 5 && hour < 12) {
      return { label: 'Early Bird', emoji: '🌅' };
    }
    if (hour >= 12 && hour < 17) {
      return { label: 'Midday Maverick', emoji: '☀️' };
    }
    if (hour >= 17 && hour < 21) {
      return { label: 'Golden Hour Hero', emoji: '🌆' };
    }
    return { label: 'Night Owl', emoji: '🦉' };
  };

  const timeOfDay = useMemo(
    () => getTimeOfDayLabel(data?.most_active_hour ?? 0),
    [data?.most_active_hour],
  );

  const activeDays = data?.total_active_days.agent ?? 0;
  const hasActivityData = activeDays > 0;

  // Title animation - exactly like impact slide
  const titleSpring = useSpring({
    opacity: phase >= 1 ? 1 : 0,
    transform:
      phase >= 2
        ? 'translateY(-280px) scale(0.5)'
        : phase >= 1
          ? 'translateY(0px) scale(1)'
          : 'translateY(50px) scale(0.8)',
    config: { tension: 120, friction: 14 },
    immediate: isCompleted,
  });

  // Subtitle animation - exactly like impact slide
  const subtitleSpring = useSpring({
    opacity: phase >= 3 ? 1 : 0,
    transform:
      phase >= 3
        ? 'translateY(-300px) scale(0.6)'
        : 'translateY(-340px) scale(0.6)',
    config: config.gentle,
    immediate: isCompleted,
  });

  // Get top 3 channels in podium order: [2nd place, 1st place, 3rd place]
  const topChannels = (data?.channel_breakdown ?? []).slice(0, 3);

  // Placeholder data for missing podium positions
  const placeholderChannels: Array<{
    channel: Channel;
    percentage: number;
    isPlaceholder: true;
  }> = [
    { channel: 'widget_chat' as Channel, percentage: 0, isPlaceholder: true },
    { channel: 'email' as Channel, percentage: 0, isPlaceholder: true },
    { channel: 'pstn_phone' as Channel, percentage: 0, isPlaceholder: true },
  ];

  // Pad real data with placeholders to always have exactly 3 items
  const channelsWithPlaceholders = [
    ...topChannels.map((c) => ({ ...c, isPlaceholder: false as const })),
    ...placeholderChannels.slice(topChannels.length),
  ].slice(0, 3);

  const podiumOrder = [
    channelsWithPlaceholders[1],
    channelsWithPlaceholders[0],
    channelsWithPlaceholders[2],
  ];

  // Animation order: 1st -> 2nd -> 3rd (phases 4, 5, 6)
  // podiumOrder indices: 0=2nd, 1=1st, 2=3rd
  // So: index 1 (1st) at phase 4, index 0 (2nd) at phase 5, index 2 (3rd) at phase 6
  const animationPhaseMap = [5, 4, 6]; // Maps podiumOrder index to phase

  // Podium springs - animate each position
  const podiumSprings = useSprings(
    3,
    podiumOrder.map((_, index) => ({
      opacity: phase >= animationPhaseMap[index] ? 1 : 0,
      transform:
        phase >= animationPhaseMap[index]
          ? 'translateY(0px) scale(1)'
          : 'translateY(100px) scale(0.8)',
      config: { tension: 120, friction: 14 },
      immediate: isCompleted,
    })),
  );

  // Podium base heights: 1st=140px, 2nd=100px, 3rd=70px
  const baseHeights = [100, 140, 70]; // Maps to podiumOrder: [2nd, 1st, 3rd]
  const baseWidths = [140, 160, 140];

  // Podium base springs - animate height growing up
  const baseSprings = useSprings(
    3,
    podiumOrder.map((_, index) => ({
      height: phase >= animationPhaseMap[index] ? baseHeights[index] : 0,
      width: baseWidths[index],
      config: { tension: 80, friction: 14 },
      immediate: isCompleted,
    })),
  );

  // Tagline spring - appears after all podium positions
  const taglineSpring = useSpring({
    opacity: phase >= 7 ? 1 : 0,
    transform:
      phase >= 7 ? 'translateY(0px) scale(1)' : 'translateY(20px) scale(0.8)',
    config: { tension: 150, friction: 12 },
    immediate: isCompleted,
  });

  // Rhythm cards springs - each card animates on its own phase
  const rhythmSprings = useSprings(3, [
    {
      opacity: phase >= 8 ? 1 : 0,
      transform:
        phase >= 8
          ? 'translateX(0px) rotate(-2deg)'
          : 'translateX(-100px) rotate(-10deg)',
      config: { tension: 100, friction: 14 },
      immediate: isCompleted,
    },
    {
      opacity: phase >= 9 ? 1 : 0,
      transform:
        phase >= 9
          ? 'translateY(0px) rotate(1deg)'
          : 'translateY(100px) rotate(5deg)',
      config: { tension: 100, friction: 14 },
      immediate: isCompleted,
    },
    {
      opacity: phase >= 10 ? 1 : 0,
      transform:
        phase >= 10
          ? 'translateX(0px) rotate(2deg)'
          : 'translateX(100px) rotate(10deg)',
      config: { tension: 100, friction: 14 },
      immediate: isCompleted,
    },
  ]);

  return (
    <SlideContainer className={isCompleted ? styles.completed : undefined}>
      <animated.div className={styles.titleWrapper} style={titleSpring}>
        <h1 className={styles.title}>YOUR STYLE</h1>
      </animated.div>

      <animated.p className={styles.subtitle} style={subtitleSpring}>
        YOUR RHYTHM, YOUR RULES
      </animated.p>

      {/* Podium section */}
      <div
        className={`${styles.podiumSection} ${phase >= 4 ? styles.visible : ''}`}
      >
        <div className={styles.podiumContainer}>
          {podiumOrder.map((channel, index) => {
            if (!channel) return null;
            const channelConfig = getChannelConfig(channel.channel);
            const isFirst = index === 1; // Center position is 1st place
            const place = index === 0 ? 2 : index === 1 ? 1 : 3;
            const isPlaceholder = channel.isPlaceholder;

            return (
              <animated.div
                key={`${channel.channel}-${index}`}
                className={`${styles.podiumItem} ${styles[`place${place}`]}`}
                style={podiumSprings[index]}
              >
                <div className={styles.podiumIcon}>
                  {isPlaceholder ? (
                    <span style={{ fontSize: 24 }}>—</span>
                  ) : (
                    <Icon name={channelConfig.icon} size={24} />
                  )}
                </div>
                <div className={styles.podiumPercentage}>
                  {phase >= animationPhaseMap[index] && (
                    <>
                      {!isPlaceholder ? (
                        <>
                          <CountUp
                            end={Math.round(channel.percentage)}
                            duration={isCompleted ? 0 : 1.5}
                            decimals={0}
                          />
                          %
                        </>
                      ) : (
                        '—'
                      )}
                    </>
                  )}
                </div>
                <div className={styles.podiumName}>
                  {isPlaceholder ? 'n/a' : channelConfig.name}
                </div>
                {isFirst && !isPlaceholder && (
                  <animated.div
                    className={styles.podiumTagline}
                    style={taglineSpring}
                  >
                    {channelConfig.tagline}
                  </animated.div>
                )}
                <animated.div
                  className={`${styles.podiumBase} ${styles[`base${place}`]}`}
                  style={baseSprings[index]}
                >
                  <span className={styles.podiumRank}>#{place}</span>
                </animated.div>
              </animated.div>
            );
          })}
        </div>
      </div>

      {/* Rhythm section */}
      <div
        className={`${styles.rhythmSection} ${phase >= 8 ? styles.visible : ''}`}
      >
        <div className={styles.rhythmGrid}>
          <animated.div
            className={`${styles.rhythmCard} ${phase >= 8 ? styles.settled : ''}`}
            style={rhythmSprings[0]}
          >
            <span className={styles.rhythmEmoji}>⚡</span>
            <span className={styles.rhythmTitle}>{`${activeDays} Days`}</span>
            <span className={styles.rhythmDetail}>Active this year</span>
          </animated.div>

          <animated.div
            className={`${styles.rhythmCard} ${phase >= 8 ? styles.settled : ''}`}
            style={rhythmSprings[1]}
          >
            <span className={styles.rhythmEmoji}>📅</span>
            <span className={styles.rhythmTitle}>
              {hasActivityData
                ? moment()
                    .isoWeekday(data?.most_active_day_of_week ?? 1)
                    .format('dddd')
                : '—'}
            </span>
            <span className={styles.rhythmDetail}>Your power day</span>
          </animated.div>

          <animated.div
            className={`${styles.rhythmCard} ${phase >= 8 ? styles.settled : ''}`}
            style={rhythmSprings[2]}
          >
            <span className={styles.rhythmEmoji}>
              {hasActivityData ? timeOfDay.emoji : '⏰'}
            </span>
            <span className={styles.rhythmTitle}>
              {hasActivityData ? timeOfDay.label : '—'}
            </span>
            <span className={styles.rhythmDetail}>
              {hasActivityData
                ? `Peak hour: ${moment()
                    .hour(data?.most_active_hour ?? 0)
                    .minute(0)
                    .format('LT')}`
                : 'Peak hour: —'}
            </span>
          </animated.div>
        </div>
      </div>
    </SlideContainer>
  );
});
